// src/components/Navbar.js
import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import { AppBar, Toolbar, Typography, Box, Button, InputBase, Menu, MenuItem, IconButton, ClickAwayListener } from '@mui/material';
import { styled } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from "../context/AuthContext";
import { useModal } from '../context/ModalContext';

const TopBar = styled(Box)({
    height: '40px',
    backgroundColor: '#142434',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 32px',
});

const StyledAppBar = styled(AppBar)({
    backgroundColor: '#ffffff',
    boxShadow: 'none',
    height: '112px',
    justifyContent: 'center',
});

const SearchBox = styled('div')({
    position: 'relative',
    height: '40px',
    border: '1px solid #E67350',
    borderRadius: '25px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    justifyContent: 'flex-end',
});

const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { loginOpen, openLogin, signupOpen, openSignup, closeLogin, closeSignup } = useModal();

    const [searchTerm, setSearchTerm] = useState('');

    const handleSearch = () => {
        if (searchTerm.trim() !== '') {
            navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
        }
    };

    // Desktop account menu
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleLogout = () => { logout(); handleMenuClose(); };

    // Mobile menu
    const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
    const openMobileMenu = Boolean(mobileAnchorEl);
    const handleMobileMenuOpen = (event) => setMobileAnchorEl(event.currentTarget);
    const handleMobileMenuClose = () => setMobileAnchorEl(null);

    const navLinks = [
        { title: 'Models', path: '/models' },
        { title: 'Resources', path: '/resources' },
        { title: 'News & Events', path: '/news-events' },
    ];

    return (
        <>
            {/* Top Bar */}
            <TopBar>
                <Button
                    component={Link}
                    to="/help"
                    sx={{
                        color: "#ffffff",
                        fontFamily: 'PT Sans, sans-serif',
                        fontWeight: 400,
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                        },
                    }}
                >
                    <HelpOutlineIcon sx={{ mr: 0.5, fontSize: '18px' }} />
                    Help
                </Button>

                {user ? (
                    <ClickAwayListener onClickAway={handleMenuClose}>
                        <Box>
                            <Button
                                onClick={handleMenuOpen}
                                sx={{
                                    color: "#ffffff",
                                    fontFamily: 'PT Sans, sans-serif',
                                    fontWeight: 400,
                                    textTransform: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    ml: 2,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <PersonOutlineIcon sx={{ mr: 0.5, fontSize: "18px" }} />
                                Hi, {user.firstname}
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={openMenu}
                                onClose={handleMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                disableScrollLock
                            >
                                <MenuItem component={Link} to="/account" onClick={handleMenuClose}>
                                    Account
                                </MenuItem>
                                <MenuItem onClick={handleLogout}>
                                    Log out
                                </MenuItem>
                            </Menu>
                        </Box>
                    </ClickAwayListener>
                ) : (
                    <Button
                        onClick={openLogin}
                        sx={{
                            color: "#ffffff",
                            fontFamily: 'PT Sans, sans-serif',
                            fontWeight: 400,
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            ml: 2,
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        <PersonOutlineIcon sx={{ mr: 0.5, fontSize: '18px' }} />
                        Login
                    </Button>
                )}
            </TopBar>

            {/* Main Navbar */}
            <StyledAppBar position="static">
                <Toolbar sx={{ justifyContent: 'space-between', paddingLeft: '16px', paddingRight: '16px', flexWrap: 'wrap' }}>
                    {/* Left: Logo + Desktop links */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box component={Link} to="/">
                            <Box
                                component="img"
                                src="/images/logoBIDT.png"
                                alt="Logo"
                                sx={{ width: { xs: '180px', sm: '240px', lg: '300px' }, height: 'auto' }}
                            />
                        </Box>

                        {/* Desktop nav */}
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                gap: { md: '40px', lg: '48px' },
                                marginLeft: { md: '40px', lg: '48px' },
                            }}
                        >
                            {navLinks.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <Typography
                                        key={item.title}
                                        component={Link}
                                        to={item.path}
                                        sx={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontWeight: isActive ? 700 : 400,
                                            fontSize: '16px',
                                            color: '#142434',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                fontWeight: 700,
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                );
                            })}
                        </Box>

                        {/* Mobile menu button */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 2 }}>
                            <IconButton onClick={handleMobileMenuOpen} size="large">
                                <MenuIcon sx={{ color: '#142434' }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Right: Search */}
                    <SearchBox
                        sx={{
                            width: { xs: '150px', sm: '180px', md: '240px' },
                            mt: { xs: 1, md: 0 },
                        }}
                    >
                        <InputBase
                            placeholder="Search…"
                            inputProps={{ 'aria-label': 'search' }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                            sx={{
                                color: '#142434',
                                fontFamily: 'PT Sans, sans-serif',
                                fontSize: '14px',
                                width: '100%',
                                ml: 1,
                            }}
                        />
                        <SearchIcon sx={{ color: '#142434', fontSize: '20px' }} />
                    </SearchBox>
                </Toolbar>
            </StyledAppBar>

            {/* Mobile Menu */}
            <Menu
                anchorEl={mobileAnchorEl}
                open={openMobileMenu}
                onClose={handleMobileMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {navLinks.map((item) => (
                    <MenuItem key={item.title} component={Link} to={item.path} onClick={handleMobileMenuClose}>
                        {item.title}
                    </MenuItem>
                ))}

                {/* {user ? (
                    <>
                        <MenuItem component={Link} to="/account" onClick={handleMobileMenuClose}>
                            Account
                        </MenuItem>
                        <MenuItem onClick={() => { handleLogout(); handleMobileMenuClose(); }}>
                            Log out
                        </MenuItem>
                    </>
                ) : (
                    <MenuItem onClick={() => { openLogin(); handleMobileMenuClose(); }}>
                        Login
                    </MenuItem>
                )} */}
            </Menu>

            <LoginModal open={loginOpen} onClose={closeLogin} />
            <SignupModal open={signupOpen} onClose={closeSignup} />
        </>
    );
};

export default Navbar;
